/**
 * Admin Users — list, search, filter, add student/admin, reset password.
 *
 * Backend contract (unchanged):
 *   GET  /api/admin/users?search=&category=&role=   → list (excludes superadmins)
 *   POST /api/auth/register                         → create
 *   POST /api/auth/reset-password  { user_id }      → reset to said@1234
 *   GET  /api/auth/student-categories               → dropdown options
 *
 * Bulk upload lives in the SuperAdmin legacy console for now.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyRound, Search, UserPlus, X } from 'lucide-react';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';

const ROLE_FILTERS = [
  { value: 'all',     label: 'All roles' },
  { value: 'student', label: 'Students' },
  { value: 'admin',   label: 'Faculty (admin)' },
];

export default function UsersPage({ ctx }) {
  const { showToast } = ctx;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('all');
  const [category, setCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [resetting, setResetting] = useState(null); // { id, name } while confirming

  const load = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (params.search) qs.set('search', params.search);
      if (params.role && params.role !== 'all') qs.set('role', params.role);
      if (params.category && params.category !== 'all') qs.set('category', params.category);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const { data } = await apiClient.get(`${endpoints.users.adminList}${suffix}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('admin/users', e);
      showToast && showToast('Failed to load users', 'err');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    apiClient.get(endpoints.auth.studentCategories).then((r) => {
      setCategories(r.data?.categories || []);
    }).catch(() => {});
  }, []);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => load({ search: q, role, category }), 300);
    return () => clearTimeout(t);
  }, [q, role, category, load]);

  const counts = useMemo(() => ({
    total:    rows.length,
    students: rows.filter((u) => u.role === 'student').length,
    admins:   rows.filter((u) => u.role === 'admin').length,
    blocked:  rows.filter((u) => !u.is_active).length,
  }), [rows]);

  const onResetPassword = async () => {
    if (!resetting) return;
    try {
      await apiClient.post(endpoints.auth.resetPassword, { user_id: resetting.id });
      showToast && showToast(`Password for ${resetting.name} reset to said@1234`, 'ok');
    } catch (e) {
      showToast && showToast(e?.response?.data?.detail || 'Reset failed', 'err');
    } finally {
      setResetting(null);
    }
  };

  return (
    <div data-testid="ap-users-page">
      <div className="ap-eyebrow">Faculty Console</div>
      <h1 className="ap-page-title">Users</h1>
      <p className="ap-page-lede">
        Add students under your supervision and reset their passwords. Blocking or
        deleting an account is a SuperAdmin action — ping them if needed.
      </p>

      <div className="ap-ind-row" role="group" aria-label="Users summary">
        <div className="ap-ind" data-testid="ap-users-count-total">
          <div className="ap-ind-label">Visible accounts</div>
          <div className="ap-ind-value">{counts.total}</div>
          <div className="ap-ind-hint">Filtered by your current search</div>
        </div>
        <div className="ap-ind" data-testid="ap-users-count-students">
          <div className="ap-ind-label">Students</div>
          <div className="ap-ind-value">{counts.students}</div>
          <div className="ap-ind-hint">Under BDS / PG programs</div>
        </div>
        <div className="ap-ind" data-testid="ap-users-count-admins">
          <div className="ap-ind-label">Faculty</div>
          <div className="ap-ind-value">{counts.admins}</div>
          <div className="ap-ind-hint">Login-holding faculty admins</div>
        </div>
        <div className="ap-ind" data-testid="ap-users-count-blocked">
          <div className="ap-ind-label"><em className="is-warn" />Blocked</div>
          <div className="ap-ind-value">{counts.blocked}</div>
          <div className="ap-ind-hint">Inactive accounts</div>
        </div>
      </div>

      <div className="ap-mt-6" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: 420 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 12, opacity: 0.5 }} />
          <input
            className="ap-input"
            style={{ paddingLeft: 30 }}
            placeholder="Search name, username, email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="ap-users-search"
          />
        </div>
        <select
          className="ap-input"
          style={{ maxWidth: 180 }}
          value={role}
          onChange={(e) => setRole(e.target.value)}
          data-testid="ap-users-role-filter"
        >
          {ROLE_FILTERS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <select
          className="ap-input"
          style={{ maxWidth: 200 }}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="ap-users-category-filter"
        >
          <option value="all">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          type="button"
          className="ap-btn ap-btn-primary"
          onClick={() => setAddOpen(true)}
          data-testid="ap-users-add-btn"
        >
          <UserPlus size={14} style={{ marginRight: 6 }} /> Add user
        </button>
      </div>

      <div className="ap-mt-6">
        {loading ? (
          <div className="ap-empty">Loading users…</div>
        ) : rows.length === 0 ? (
          <div className="ap-empty" data-testid="ap-users-empty">No users match your filters.</div>
        ) : (
          <div className="ap-table-wrap" data-testid="ap-users-table">
            <table className="ap-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username · Email</th>
                  <th>Role</th>
                  <th>Category / Roll</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id} data-testid={`ap-users-row-${u.id}`}>
                    <td style={{ fontWeight: 500, color: 'var(--diis-ink)' }}>{u.full_name || '—'}</td>
                    <td>
                      <div>@{u.username}</div>
                      <div className="ap-subtle">{u.email || '—'}</div>
                    </td>
                    <td>
                      <span style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: u.role === 'admin' ? 'var(--diis-accent)' : 'var(--diis-ink-subtle)' }}>
                        {u.role}
                      </span>
                    </td>
                    <td>
                      <div>{u.student_category || '—'}</div>
                      {u.roll_number && <div className="ap-subtle">Roll {u.roll_number}</div>}
                    </td>
                    <td>
                      {u.is_active ? (
                        <span style={{ color: '#0F766E', fontSize: 12 }}>● Active</span>
                      ) : (
                        <span style={{ color: '#B04A3C', fontSize: 12 }}>● Blocked</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        className="ap-btn ap-btn-ghost ap-btn-sm"
                        onClick={() => setResetting({ id: u.id, name: u.full_name || u.username })}
                        title="Reset password to said@1234"
                        data-testid={`ap-users-reset-${u.id}`}
                      >
                        <KeyRound size={13} style={{ marginRight: 4 }} /> Reset
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {addOpen && (
        <AddUserModal
          categories={categories}
          onClose={() => setAddOpen(false)}
          onCreated={() => { setAddOpen(false); load({ search: q, role, category }); showToast && showToast('User created', 'ok'); }}
          onError={(msg) => showToast && showToast(msg, 'err')}
        />
      )}

      {resetting && (
        <div className="ap-modal-scrim" onClick={() => setResetting(null)} data-testid="ap-users-reset-modal">
          <div className="ap-modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="ap-modal-header">
              <div>
                <h3 style={{ margin: 0, fontFamily: 'var(--diis-font-display)', fontWeight: 500, fontSize: 20 }}>Reset password?</h3>
                <p className="ap-subtle" style={{ margin: '4px 0 0', fontSize: 12 }}>
                  {resetting.name}'s password will be reset to the default <code>said@1234</code>.
                  They'll be forced to change it on next login.
                </p>
              </div>
              <button type="button" className="ap-btn ap-btn-ghost ap-btn-sm" onClick={() => setResetting(null)} aria-label="Close">
                <X size={14} />
              </button>
            </div>
            <div className="ap-modal-footer">
              <button type="button" className="ap-btn ap-btn-ghost" onClick={() => setResetting(null)}>Cancel</button>
              <button type="button" className="ap-btn ap-btn-primary" onClick={onResetPassword} data-testid="ap-users-reset-confirm">
                Reset password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddUserModal({ categories, onClose, onCreated, onError }) {
  const [form, setForm] = useState({
    username: '', password: '', email: '', role: 'student',
    full_name: '', student_category: '', roll_number: '',
  });
  const [saving, setSaving] = useState(false);

  const canSave = form.username && form.password && form.email && form.full_name &&
    (form.role !== 'student' || form.student_category);

  const submit = async () => {
    setSaving(true);
    try {
      await apiClient.post(endpoints.auth.register, form);
      onCreated();
    } catch (e) {
      onError(e?.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="ap-modal-scrim" onClick={onClose} data-testid="ap-users-add-modal">
      <div className="ap-modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="ap-modal-header">
          <div>
            <h3 style={{ margin: 0, fontFamily: 'var(--diis-font-display)', fontWeight: 500, fontSize: 20 }}>Add user</h3>
            <p className="ap-subtle" style={{ margin: '4px 0 0', fontSize: 12 }}>Create a student or fellow faculty account.</p>
          </div>
          <button type="button" className="ap-btn ap-btn-ghost ap-btn-sm" onClick={onClose} aria-label="Close"><X size={14} /></button>
        </div>
        <div className="ap-modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ gridColumn: '1 / -1' }}>
            <div className="ap-field-label">Role</div>
            <select className="ap-input" value={form.role} onChange={(e) => setField('role', e.target.value)} data-testid="ap-users-add-role">
              <option value="student">Student</option>
              <option value="admin">Faculty (admin)</option>
            </select>
          </label>
          <label>
            <div className="ap-field-label">Full name</div>
            <input className="ap-input" value={form.full_name} onChange={(e) => setField('full_name', e.target.value)} data-testid="ap-users-add-fullname" />
          </label>
          <label>
            <div className="ap-field-label">Username</div>
            <input className="ap-input" value={form.username} onChange={(e) => setField('username', e.target.value)} data-testid="ap-users-add-username" />
          </label>
          <label>
            <div className="ap-field-label">Email</div>
            <input type="email" className="ap-input" value={form.email} onChange={(e) => setField('email', e.target.value)} data-testid="ap-users-add-email" />
          </label>
          <label>
            <div className="ap-field-label">Password</div>
            <input type="password" className="ap-input" value={form.password} onChange={(e) => setField('password', e.target.value)} placeholder="min 6 chars" data-testid="ap-users-add-password" />
          </label>
          {form.role === 'student' && (
            <>
              <label>
                <div className="ap-field-label">Category</div>
                <select className="ap-input" value={form.student_category} onChange={(e) => setField('student_category', e.target.value)} data-testid="ap-users-add-category">
                  <option value="">— select —</option>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label>
                <div className="ap-field-label">Roll number</div>
                <input className="ap-input" value={form.roll_number} onChange={(e) => setField('roll_number', e.target.value)} data-testid="ap-users-add-roll" />
              </label>
            </>
          )}
        </div>
        <div className="ap-modal-footer">
          <button type="button" className="ap-btn ap-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="ap-btn ap-btn-primary" onClick={submit} disabled={!canSave || saving} data-testid="ap-users-add-submit">
            {saving ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </div>
    </div>
  );
}
